<template>
  <section class="real-time-container">
    <transition name="fade">
      <header v-if="currShift" class="real-time-toolbar" :class="{ 'is-rtl': isRTL }">
        <div class="real-time-toolbar-main">
          <TopInfo :currShift="currShift" :dayCounts="dayCounts" :showDayCounts="canSeeCleaning" />
        </div>
        <div class="real-time-toolbar-actions">
          <div class="room-search" role="search">
            <label class="room-search-label" for="real-time-room-search">{{ $t('search') }}</label>
            <input
              id="real-time-room-search"
              class="room-search-input"
              type="text"
              v-model="roomSearchText"
              :placeholder="$t('room_name')"
              autocomplete="off"
            />
          </div>
          <div class="cleaner-filter" role="group" :aria-label="$t('filter_by_cleaner')">
            <label class="cleaner-filter-label" for="real-time-cleaner-select">{{ $t('filter_by_cleaner') }}</label>
            <div class="cleaner-filter-control" :class="{ 'is-disabled': loadingSiteContacts }">
              <select
                id="real-time-cleaner-select"
                class="cleaner-filter-select"
                :value="selectedContactId == null ? '' : selectedContactId"
                @change="onSelectCleaner($event)"
                :disabled="loadingSiteContacts"
                :aria-busy="loadingSiteContacts"
              >
                <option value="">{{ $t('all_cleaners') }}</option>
                <option v-for="c in siteContacts" :key="c.id" :value="c.id">{{ c.username }}</option>
              </select>
            </div>
          </div>
          <button
            v-if="hasVisibleTab && ($route.name === 'realTime' || $route.name === 'maintenanceRealTime' || $route.name === 'taskRealTime')"
            type="button"
            class="toolbar-fullscreen-btn"
            @click="openFullScreen"
            :title="$t('full_screen')"
          >
            <img :src="require('@/assets/full_screen.svg')" alt="" />
            <span class="toolbar-fullscreen-btn-text">{{ $t('full_screen') }}</span>
          </button>
        </div>
      </header>
    </transition>
    <section v-if="!hasVisibleTab">{{ $t('no_real_time_tabs_permitted') }}</section>
    <Tabs v-else :isRTL="isRTL" syncRoute class="tabs-container" ref="tabs">
      <Tab v-if="canSeeCleaning" :label="$t('cleaning')" to="/real-time" exact>
        <Loading v-if="loadingShift" />
        <template v-else-if="currShift">
          <Loading v-if="!realTimeData" />
          <RealTimeTable v-else :isMaintenance="false" :realTimeData="realTimeData" :roomSearchText="roomSearchText" :maxSla="currShift.max_sla" :companyGmt="currShift.gmt" :siteId="filterSelected?.site || currentSiteInfo?.id" @closeClick="closeRoomSla" />
        </template>
        <section v-else>No Shift Please Check If Site Is Selected</section>
      </Tab>
      <Tab v-if="canSeeMaintenance" :label="$t('maintenance')" to="/real-time/maintenance">
        <Loading v-if="loadingShift" />
        <template v-else-if="currShift">
          <Loading v-if="!maintenanceRealTimeData" />
          <RealTimeTable
            v-else
            :isMaintenance="true"
            :realTimeData="maintenanceRealTimeData"
            :roomSearchText="roomSearchText"
            :maxSla="currShift.max_sla"
            :companyGmt="currShift.gmt"
            :siteId="filterSelected?.site || currentSiteInfo?.id"
            @closeClick="
              roomId => {
                closeRoomSla(roomId, true)
              }
            "
          />
        </template>
        <section v-else>No Shift Please Check If Site Is Selected</section>
      </Tab>
      <Tab v-if="isShowTaskTab" :label="$t('task')" to="/real-time/task">
        <Loading v-if="loadingShift" />
        <template v-else-if="currShift">
          <Loading v-if="!taskRealTimeData" />
          <RealTimeTable v-else-if="taskRealTimeData.length > 0" :isTask="true" :realTimeData="taskRealTimeData" :roomSearchText="roomSearchText" :companyGmt="currShift.gmt" :siteId="filterSelected?.site || currentSiteInfo?.id" @startTask="startTask" @completeTask="completeTask" @deleteOpenTask="deleteOpenTask" />
          <section v-else>{{ $t('no_data_available') }}</section>
        </template>
        <section v-else>No Shift Please Check If Site Is Selected</section>
      </Tab>
    </Tabs>
  </section>
</template>

<script>
import Tabs from '../../common/components/Tabs/Tabs'
import Tab from '../../common/components/Tabs/Tab'
import Loading from '../../common/components/Loading'
import TopInfo from '../components/TopInfo'
import RealTimeTable from './RealTimeTable'
import realTimeService from '../services/realTimeService'
import taskService from '@/modules/task/services/taskService.js'
import shiftService from '../../control-panel/services/shiftService'
import sensorService from '../../control-panel/services/sensorService'
import signalService from '../../../survey/services/signalService'
import swalService from '@/modules/common/services/swalService'
import { getFilterdContacts } from '../../control-panel/services/contactService'
import { localeData } from '@/modules/common/services/localeService'
import { REAL_TIME_CLEANING_PERMISSION, REAL_TIME_MAINTENANCE_PERMISSION, canSeeRealTimeTab } from '@/modules/common/consts/consts'
import { mapGetters } from 'vuex'

import io from 'socket.io-client/dist/socket.io'

const SOCKET_URL = process.env.NODE_ENV === 'production' ? '/' : 'http://localhost:3000'

export default {
  data() {
    return {
      realTimeData: null,
      maintenanceRealTimeData: null,
      taskRealTimeData: null,
      cleandexData: null,
      currShift: null,
      socket: null,
      siteType: null,
      /** True while fetching shift (getCurrentShift); show Loading until we know if there's a shift or not */
      loadingShift: false,
      siteContacts: [],
      loadingSiteContacts: false,
      /** @type {number|null} */
      selectedContactId: null,
      /** When set, socket and REST payloads are restricted to these room ids */
      allowedRoomIdsSet: null,
      _loadedRealTimeSiteId: null,
      roomSearchText: '',
      dayCounts: { complaints: null, callCleaners: null },
      dayCountsInterval: null,
    }
  },
  methods: {
    async loadDayCounts(siteId) {
      if (!this.canSeeCleaning) return
      if (!siteId) {
        this.dayCounts = { complaints: null, callCleaners: null }
        return
      }
      try {
        this.dayCounts = await realTimeService.getRealTimeDayCounts(siteId)
      } catch (err) {
        console.error('Failed to load real-time day counts', err)
      }
    },
    startDayCountsPolling(siteId) {
      if (this.dayCountsInterval) clearInterval(this.dayCountsInterval)
      if (!this.canSeeCleaning) return
      this.dayCountsInterval = setInterval(() => {
        this.loadDayCounts(siteId)
      }, 60 * 1000)
    },
    stopDayCountsPolling() {
      if (this.dayCountsInterval) {
        clearInterval(this.dayCountsInterval)
        this.dayCountsInterval = null
      }
    },
    openFullScreen() {
      const tabsEl = this.$refs.tabs.$el
      //browser compatibility
      if (tabsEl.msRequestFullscreen) tabsEl.msRequestFullscreen()
      else if (tabsEl.mozRequestFullScreen) tabsEl.mozRequestFullScreen()
      else if (tabsEl.webkitRequestFullscreen) tabsEl.webkitRequestFullscreen()
      else tabsEl.requestFullscreen()
    },
    async closeRoomSla(roomId, isMaintenance = false) {
      const { device_code, type } = await sensorService.getSensorByRoomId(roomId)
      if (!device_code) {
        swalService.errorMsg('This room has no device code attached to it')
        return
      }
      try {
        await signalService.sendSignal(isMaintenance ? 43 : 48, device_code, false, false, type)
        const currRealtimeData = isMaintenance ? 'maintenanceRealTimeData' : 'realTimeData'
        const foundIndex = this[currRealtimeData].findIndex(room => room.id === roomId)
        this[currRealtimeData][foundIndex].isOpen = false
        this[currRealtimeData][foundIndex].complaints = []
      } catch (err) {
        console.error('failed sending signal!')
      }
    },
    async startTask(slaId) {
      try {
        const foundIndex = this.taskRealTimeData.findIndex(task => task.slaId === slaId)
        const task = this.taskRealTimeData[foundIndex]
        if (new Date(task.date) > new Date()) {
          swalService.errorMsg('You cannot start a task before its open time')
          return
        }
        if (task.isStarted) {
          swalService.errorMsg('You cannot start a task that is already started')
          return
        }
        const approval = await taskService.startTask(slaId)
        if (approval) {
          this.taskRealTimeData[foundIndex].openTime = new Date()
          this.taskRealTimeData[foundIndex].isStarted = true
        }
      } catch (err) {
        console.error(err)
      }
    },
    async completeTask(slaId) {
      try {
        const foundIndex = this.taskRealTimeData.findIndex(task => task.slaId === slaId)
        const task = this.taskRealTimeData[foundIndex]
        const { isStarted, closeTime, startTime } = task
        if (!isStarted) {
          swalService.errorMsg('You cannot complete a task before starting it')
          return
        }
        const approval = await taskService.completeTask(slaId)
        if (approval) {
          this.taskRealTimeData[foundIndex].closeTime = new Date()
          this.taskRealTimeData[foundIndex].isOpen = false
          this.taskRealTimeData[foundIndex].responseTime = new Date(closeTime) - new Date(startTime)
        }
      } catch (err) {
        console.error(err)
      }
    },
    async deleteOpenTask(slaId) {
      const confirmed = await swalService.confirmMsg()
      if (!confirmed.value) return
      try {
        await taskService.deleteOpenTask(slaId)
        this.taskRealTimeData = this.taskRealTimeData.filter((task) => task.slaId !== slaId)
        swalService.deletedMsg()
      } catch (err) {
        console.error(err)
        swalService.errorMsg(err?.message || 'Failed to delete task')
      }
    },
    clearRealTimeState() {
      this.currShift = null
      this.loadingShift = false
      this.realTimeData = null
      this.maintenanceRealTimeData = null
      this.taskRealTimeData = null
      this.cleandexData = null
      this.siteContacts = []
      this.selectedContactId = null
      this.allowedRoomIdsSet = null
      this._loadedRealTimeSiteId = null
      this.roomSearchText = ''
      this.dayCounts = { complaints: null, callCleaners: null }
      this.stopDayCountsPolling()
      if (this.socket) {
        this.socket.disconnect()
        this.socket = null
      }
    },
    filterRowsByCleanerScope(rows) {
      if (!rows) return rows
      if (this.allowedRoomIdsSet == null) return rows
      if (this.allowedRoomIdsSet.size === 0) return []
      return rows.filter((r) => this.allowedRoomIdsSet.has(r.id))
    },
    async refreshAllowedRoomIds(siteId) {
      if (this.selectedContactId == null || !siteId) {
        this.allowedRoomIdsSet = null
        return
      }
      const ids = await realTimeService.getRealTimeRoomScope(siteId, this.selectedContactId)
      this.allowedRoomIdsSet = new Set(ids)
    },
    async onSelectCleaner(ev) {
      const raw = ev.target.value
      this.selectedContactId = raw === '' ? null : Number(raw)
      await this.onCleanerFilterChange()
    },
    async onCleanerFilterChange() {
      const siteId = this.filterSelected?.site || this.currentSiteInfo?.id
      if (!siteId || !this.currShift) return
      await this.refreshAllowedRoomIds(siteId)
      const timeFrom = realTimeService.filterFromByShiftStartHour(this.currShift.start_hour, this.currShift.gmt)
      const cid = this.selectedContactId
      const taskParams = {
        siteId,
        timeFrom,
        language: localeData.language,
        shiftId: this.currShift.id
      }
      if (cid != null) {
        taskParams.contactId = cid
      }
      try {
        await this.loadPanelLists(siteId, timeFrom, cid, taskParams)
      } catch (err) {
        console.error(err)
      }
    },
    /** Loads only the lists of the tabs this user may see; a hidden tab keeps null data. */
    async loadPanelLists(siteId, timeFrom, contactId, taskParams) {
      const [cleanData, maintenanceData, taskData, cleandexData] = await Promise.all([
        this.canSeeCleaning ? realTimeService.getRealTimeData(siteId, timeFrom, 'clean', false, false, contactId) : null,
        this.canSeeMaintenance ? realTimeService.getRealTimeData(siteId, timeFrom, 'maintenance', false, true, contactId) : null,
        taskService.getTaskRealTimeData(taskParams),
        this.canSeeCleaning ? realTimeService.getCleandexData(siteId).catch(() => []) : null
      ])
      this.cleandexData = cleandexData
      this.realTimeData = cleanData ? this.mergeCleandexData(cleanData.sort((a, b) => b.isOpen - a.isOpen)) : null
      this.maintenanceRealTimeData = maintenanceData ? maintenanceData.sort((a, b) => b.isOpen - a.isOpen) : null
      this.taskRealTimeData = taskData.sort((a, b) => b.isOpen - a.isOpen)
    },
    async loadRealTimeData(siteId) {
      if (!siteId) return
      if (this._loadedRealTimeSiteId !== siteId) {
        this.selectedContactId = null
        this.allowedRoomIdsSet = null
        this.siteContacts = []
      }
      this._loadedRealTimeSiteId = siteId
      this.loadingShift = true
      const shift = await shiftService.getCurrentShift(siteId)
      const site = this.currentSiteInfo
      if (!site) {
        this.loadingShift = false
        return
      }
      this.siteType = site.type
      if (!shift) {
        this.currShift = null
        this.loadingShift = false
        return
      }
      if (this.socket) this.socket.disconnect()
      this.socket = io.connect(SOCKET_URL, {
        transports: ['websocket'],
        pollingInterval: 10000
      })
      const timeFrom = realTimeService.filterFromByShiftStartHour(shift.start_hour, shift.gmt)
      this.socket.emit('site-id', { siteId, timeFrom: timeFrom.toUTCString() })
      // The server only sends the lists this user may read, so a hidden tab's key is absent.
      this.socket.on('site-data', async ({ cleanRealTimeData, maintenanceRealTimeData }) => {
        if (this.canSeeMaintenance) {
          const maintSorted = maintenanceRealTimeData ? maintenanceRealTimeData.sort((a, b) => b.isOpen - a.isOpen) : null
          this.maintenanceRealTimeData = this.filterRowsByCleanerScope(maintSorted)
        }
        if (!this.canSeeCleaning) return
        if (cleanRealTimeData) {
          const siteIdInner = this.filterSelected?.site || this.currentSiteInfo?.id
          if (siteIdInner) {
            const cleandexData = await realTimeService.getCleandexData(siteIdInner).catch(() => [])
            this.cleandexData = cleandexData
            this.loadDayCounts(siteIdInner)
          }
          const cleanSorted = cleanRealTimeData.sort((a, b) => b.isOpen - a.isOpen)
          const cleanScoped = this.filterRowsByCleanerScope(cleanSorted)
          this.realTimeData = this.mergeCleandexData(cleanScoped)
        } else {
          this.realTimeData = null
        }
      })

      this.realTimeData = null
      this.maintenanceRealTimeData = null
      this.taskRealTimeData = null
      try {
        this.loadingSiteContacts = true
        try {
          this.siteContacts = await getFilterdContacts({ siteId }).catch(() => [])
        } finally {
          this.loadingSiteContacts = false
        }
        await this.refreshAllowedRoomIds(siteId)
        const cid = this.selectedContactId
        const taskParams = {
          siteId,
          timeFrom,
          language: localeData.language,
          shiftId: shift.id
        }
        if (cid != null) {
          taskParams.contactId = cid
        }
        // Past visits (visits_since_cleaned / room.pastVisits): NOT from sla/real-time when includePastVisits is false.
        // They come from GET sla/cleandex/:siteId → each room's debug.pastVisits (backend uses getPastVisitsMapForRoomIds;
        // response is Redis-cached ~60s). mergeCleandexData copies debug.pastVisits onto pastVisits for the table.
        // Socket 'site-data' uses the same merge after re-fetching cleandex.
        await this.loadPanelLists(siteId, timeFrom, cid, taskParams)
        this.currShift = shift
        await this.loadDayCounts(siteId)
        this.startDayCountsPolling(siteId)
      } finally {
        this.loadingShift = false
      }
    },
    /** Merges cleandex score + debug; sets pastVisits from cleandex.debug.pastVisits (see loadRealTimeData note). */
    mergeCleandexData(realTimeData) {
      if (!realTimeData || !this.cleandexData || !this.cleandexData.length) return realTimeData
      const cleandexMap = new Map()
      this.cleandexData.forEach(room => {
        if (room.id != null) cleandexMap.set(room.id, room)
      })
      return realTimeData.map(room => {
        const cdx = cleandexMap.get(room.id)
        if (cdx) {
          const pastVisits = cdx.debug?.pastVisits ?? room.pastVisits
          return {
            ...room,
            cleandex: cdx.cleandex,
            debug: cdx.debug,
            pastVisits,
            cleandexWarningLevel: cdx.cleandexWarningLevel,
            cleandexCallOpen: cdx.cleandexCallOpen,
            cleandexCallDue: cdx.cleandexCallDue,
          }
        }
        return room
      })
    }
  },
  destroyed() {
    this.stopDayCountsPolling()
    if (this.socket) this.socket.disconnect()
  },
  watch: {
    tabRedirectPath: {
      handler(path) {
        if (path) this.$router.replace(path)
      },
      immediate: true
    },
    'filterSelected.site': {
      handler(siteId) {
        if (siteId) {
          this.loadRealTimeData(siteId)
        } else {
          this.clearRealTimeState()
        }
      },
      immediate: true
    },
    'filterSelected.company': {
      handler() {
        this.clearRealTimeState()
        this.$nextTick(() => {
          if (this.filterSelected && this.filterSelected.site) {
            this.loadRealTimeData(this.filterSelected.site)
          }
        })
      }
    },
  },
  computed: {
    ...mapGetters(['isRTL', 'loggedUser', 'currentSiteInfo', 'filterSelected']),
    routeName() {
      return this.$route.name
    },
    isShowTaskTab() {
      return this.siteType === 'task' || this.siteType === 'cleandex'
    },
    userRoles() {
      return Array.isArray(this.loggedUser?.roles) ? this.loggedUser.roles : []
    },
    canSeeCleaning() {
      return canSeeRealTimeTab(this.userRoles, REAL_TIME_CLEANING_PERMISSION)
    },
    canSeeMaintenance() {
      return canSeeRealTimeTab(this.userRoles, REAL_TIME_MAINTENANCE_PERMISSION)
    },
    hasVisibleTab() {
      return this.canSeeCleaning || this.canSeeMaintenance || this.isShowTaskTab
    },
    /** Where to send a user who opened a tab they may not see; null when the current tab is fine. */
    tabRedirectPath() {
      const visibleByRoute = { realTime: this.canSeeCleaning, maintenanceRealTime: this.canSeeMaintenance }
      if (!(this.routeName in visibleByRoute) || visibleByRoute[this.routeName]) return null
      if (this.canSeeCleaning) return '/real-time'
      if (this.canSeeMaintenance) return '/real-time/maintenance'
      if (this.isShowTaskTab) return '/real-time/task'
      return null
    },
  },
  components: { Tabs, Tab, RealTimeTable, Loading, TopInfo }
}
</script>

<style lang="scss">
@use "@/styles/mixins" as *;

.real-time-container {
  background-color: white;
  min-height: 100%;

  .md-tabs-content {
    overflow: clip !important;
  }

  .tabs-container:fullscreen,
  .tabs-container:-webkit-full-screen,
  .tabs-container:-moz-full-screen,
  .tabs-container:-ms-fullscreen {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background-color: white;

    .md-tabs-content {
      flex: 1;
      min-height: 0;
      height: auto !important;
      overflow-y: auto !important;
    }
  }
  .real-time-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 14px;
    padding: 6px 14px 8px;
    margin: 0;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.9) inset;
    z-index: 11;
    position: relative;

    .room-search {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 8px;
      flex-shrink: 0;
    }
    .room-search-label {
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #64748b;
      white-space: nowrap;
      margin: 0;
      cursor: pointer;
    }
    .room-search-input {
      min-width: 132px;
      max-width: 200px;
      min-height: 30px;
      padding: 5px 9px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.2;
      color: #111827;
      background: #fff;
      box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      &:hover {
        border-color: #94a3b8;
      }
      &:focus {
        outline: none;
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.18);
      }
      &::placeholder {
        color: #94a3b8;
      }
    }
    .cleaner-filter {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 8px;
      flex-shrink: 0;
    }
    .cleaner-filter-label {
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #64748b;
      white-space: nowrap;
      margin: 0;
      cursor: pointer;
    }
    .cleaner-filter-control {
      position: relative;
      display: inline-flex;
      align-items: center;
      min-height: 30px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: #fff;
      box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      &:hover:not(.is-disabled) {
        border-color: #94a3b8;
      }
      &:focus-within:not(.is-disabled) {
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.18);
      }
      &.is-disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }
      &::after {
        content: '';
        position: absolute;
        right: 9px;
        top: 50%;
        margin-top: -2px;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 5px solid #64748b;
        pointer-events: none;
        @include rtl {
          right: auto;
          left: 9px;
        }
      }
    }
    .cleaner-filter-select {
      appearance: none;
      -webkit-appearance: none;
      min-width: 132px;
      max-width: 180px;
      padding: 5px 28px 5px 9px;
      @include rtl {
        padding: 5px 9px 5px 28px;
      }
      border: none;
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.2;
      color: #111827;
      background: transparent;
      cursor: pointer;
      &:disabled {
        cursor: not-allowed;
      }
    }

    .toolbar-fullscreen-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 30px;
      padding: 0 10px;
      background: #fff;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 600;
      color: #475569;
      box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
      transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
      img {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
      }
      &:hover {
        background: #f8fafc;
        border-color: #94a3b8;
      }
      &:focus {
        outline: none;
        border-color: #6366f1;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.18);
      }
      &:active {
        transform: scale(0.98);
      }
    }

    .toolbar-fullscreen-btn-text {
      white-space: nowrap;
    }

    @media (max-width: 520px) {
      .toolbar-fullscreen-btn-text {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
      .toolbar-fullscreen-btn {
        padding: 0 12px;
      }
    }
  }

  .real-time-toolbar-main {
    flex: 1 1 200px;
    min-width: 0;
  }

  .real-time-toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 12px;
    flex-shrink: 0;
    margin-inline-start: auto;
  }

  .real-time-toolbar.is-rtl .real-time-toolbar-actions {
    margin-inline-start: 0;
    margin-inline-end: auto;
  }

  @media (max-width: 720px) {
    .real-time-toolbar-actions {
      margin-inline-start: 0;
      width: 100%;
      justify-content: flex-start;
    }
    .real-time-toolbar.is-rtl .real-time-toolbar-actions {
      margin-inline-end: 0;
    }
  }

  @media (max-width: 768px) {
    .real-time-toolbar {
      padding: 8px 10px;
      gap: 10px;
    }

    .room-search,
    .cleaner-filter {
      width: 100%;
    }

    .room-search-input,
    .cleaner-filter-control,
    .cleaner-filter-select {
      width: 100%;
      max-width: none;
    }

    .tabs-container {
      ::v-deep .md-tabs-navigation {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

      ::v-deep .md-tabs-content {
        padding: 8px 10px 12px;
      }
    }
  }
}
</style>
